import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { IntakePhotos } from "@/components/IntakePhotos";
import { SlabTracer, polygonSqFt } from "@/components/SlabTracer";
import { CUSTOMER_GROUPS } from "@/pages/Customers";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSlabOptions, withCurrent } from "@/lib/slabOptions";
import { shrinkAll } from "@/lib/shrinkImage";

const EMPTY = {
  slab_color: "",
  material: "",
  thickness: "3cm",
  length_in: "",
  width_in: "",
  location: "",
  source_reservation_id: "none",
  status: "Available",
  sale_price_per_sqft: "",
  group_prices: {},
  visualiser_enabled: true,
  notes: "",
};

export default function RemnantForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const slabOptions = useSlabOptions();
  const THICKNESSES = withCurrent(slabOptions.thicknesses, form.thickness);
  const MATERIALS = withCurrent(slabOptions.materials, form.material);
  const [points, setPoints] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState("");
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/reservations").then(({ data }) => setJobs(data));
    if (!id) return;
    api.get(`/remnants/${id}`).then(({ data }) => {
      setForm({
        ...EMPTY,
        ...data,
        source_reservation_id: data.source_reservation_id || "none",
        sale_price_per_sqft: data.sale_price_per_sqft ?? "",
        group_prices: data.group_prices || {},
      });
      setPoints(data.polygon || []);
    });
  }, [id]);

  const L = Number(form.length_in) || 0;
  const W = Number(form.width_in) || 0;

  const pickJob = (value) => {
    if (value === "none") {
      setForm((f) => ({ ...f, source_reservation_id: "none" }));
      return;
    }
    const job = jobs.find((j) => j.id === value);
    setForm((f) => ({
      ...f,
      source_reservation_id: value,
      slab_color: job?.slab_color || f.slab_color,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const job = jobs.find((j) => j.id === form.source_reservation_id);
    const payload = {
      slab_color: form.slab_color,
      thickness: form.thickness,
      material: form.material || "",
      finish: form.finish || "",
      brand: form.brand || "",
      supplier: form.supplier || "",
      source_slab_id: form.source_slab_id || null,
      source_slab_code: form.source_slab_code || "",
      length_in: L,
      width_in: W,
      usable_sqft: polygonSqFt(points, L, W),
      polygon: points,
      location: form.location,
      source_reservation_id:
        form.source_reservation_id === "none"
          ? null
          : form.source_reservation_id,
      source_tag: job?.tag || "",
      customer_name: job?.customer_name || "",
      status: form.status,
      sale_price_per_sqft:
        form.sale_price_per_sqft === ""
          ? null
          : Number(form.sale_price_per_sqft),
      group_prices: Object.fromEntries(
        Object.entries(form.group_prices || {})
          .filter(([, v]) => v !== "" && v !== null)
          .map(([k, v]) => [k, Number(v)]),
      ),
      visualiser_enabled: form.visualiser_enabled !== false,
      notes: form.notes,
    };
    try {
      const { data } = id
        ? await api.put(`/remnants/${id}`, payload)
        : await api.post("/remnants", payload);
      for (const file of await shrinkAll(pendingPhotos)) {
        const fd = new FormData();
        fd.append("file", file);
        if (file.enhanced) fd.append("enhanced", "true");
        await api.post(`/remnants/${data.id}/photos`, fd);
      }
      toast.success(id ? "Remnant updated" : `Remnant ${data.code} logged`);
      navigate(`/inventory/remnants/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title={id ? "Edit remnant" : "Remnant intake"} />
      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-32 pt-6">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-400 transition-colors duration-150 hover:text-white"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {id ? "Edit Remnant" : "Remnant Intake"}
        </h1>

        <form
          onSubmit={submit}
          className="mt-8 space-y-5"
          data-testid="remnant-form"
        >
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Source Job
            </Label>
            <Select value={form.source_reservation_id} onValueChange={pickJob}>
              <SelectTrigger
                data-testid="source-job-select"
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
              >
                <SelectValue placeholder="Optional — link to a job" />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                <SelectItem value="none" data-testid="source-job-none">
                  No source job
                </SelectItem>
                {jobs.map((j) => (
                  <SelectItem
                    key={j.id}
                    value={j.id}
                    data-testid={`source-job-${j.tag}`}
                  >
                    {j.tag} — {j.customer_name} ({j.slab_color})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Slab Color
              </Label>
              <Input
                data-testid="remnant-color-input"
                required
                value={form.slab_color}
                onChange={(e) =>
                  setForm({ ...form, slab_color: e.target.value })
                }
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
                placeholder="Calacatta Gold"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Thickness
              </Label>
              <Select
                value={form.thickness}
                onValueChange={(v) => setForm({ ...form, thickness: v })}
              >
                <SelectTrigger
                  data-testid="thickness-select"
                  className="h-12 border-zinc-800 bg-zinc-900 text-base"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {THICKNESSES.map((t) => (
                    <SelectItem
                      key={t}
                      value={t}
                      data-testid={`thickness-${t}`}
                    >
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Material Category
            </Label>
            <Select
              value={form.material || "none"}
              onValueChange={(v) =>
                setForm({ ...form, material: v === "none" ? "" : v })
              }
            >
              <SelectTrigger
                data-testid="remnant-material-select"
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
              >
                <SelectValue placeholder="Pick a material category" />
              </SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">
                <SelectItem value="none" data-testid="remnant-material-none">
                  No category
                </SelectItem>
                {MATERIALS.map((m) => (
                  <SelectItem
                    key={m}
                    value={m}
                    data-testid={`remnant-material-${m}`}
                  >
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Length (in)
              </Label>
              <Input
                data-testid="remnant-length-input"
                type="number"
                step="0.1"
                required
                value={form.length_in}
                onChange={(e) =>
                  setForm({ ...form, length_in: e.target.value })
                }
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
                placeholder="72"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Width (in)
              </Label>
              <Input
                data-testid="remnant-width-input"
                type="number"
                step="0.1"
                required
                value={form.width_in}
                onChange={(e) => setForm({ ...form, width_in: e.target.value })}
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
                placeholder="38"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Rack / Location
              </Label>
              <Input
                data-testid="remnant-location-input"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="h-12 border-zinc-800 bg-zinc-900 text-base"
                placeholder="A-12"
              />
            </div>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            {L > 0 && W > 0 ? (
              <SlabTracer
                points={points}
                onChange={setPoints}
                lengthIn={L}
                widthIn={W}
              />
            ) : (
              <p className="text-sm text-zinc-500">
                Enter length and width to draw the slab and trace its usable
                field.
              </p>
            )}
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-zinc-400">
                Status
              </Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger
                  data-testid="remnant-status-select"
                  className="h-12 border-zinc-800 bg-zinc-900 text-base"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-zinc-800 bg-zinc-900">
                  {["Available", "Reserved", "Used"].map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`remnant-status-${s}`}
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Sale price per sq ft ($)
            </Label>
            <Input
              data-testid="remnant-sale-price-input"
              type="number"
              step="0.01"
              value={form.sale_price_per_sqft}
              onChange={(e) =>
                setForm({ ...form, sale_price_per_sqft: e.target.value })
              }
              placeholder="45.00"
              className="h-12 border-zinc-800 bg-zinc-900 text-base"
            />
          </div>

          <div
            className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="remnant-group-prices"
          >
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Price per sq ft by account group
            </div>
            <p className="mt-1 text-xs text-zinc-600">
              Leave blank to use the sale price with that group&apos;s discount
              from Settings → Customer portal.
            </p>
            <div className="mt-4 grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {CUSTOMER_GROUPS.map((g) => (
                <div key={g} className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-zinc-400">
                    {g}
                  </Label>
                  <Input
                    data-testid={`remnant-group-price-${g}`}
                    type="number"
                    step="0.01"
                    placeholder="—"
                    value={(form.group_prices || {})[g] ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        group_prices: {
                          ...(f.group_prices || {}),
                          [g]: e.target.value,
                        },
                      }))
                    }
                    className="h-12 border-zinc-800 bg-zinc-950 text-base"
                  />
                </div>
              ))}
            </div>
          </div>

          <label
            data-testid="remnant-visualiser-toggle"
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          >
            <input
              type="checkbox"
              data-testid="remnant-visualiser-input"
              checked={form.visualiser_enabled !== false}
              onChange={(e) =>
                setForm({ ...form, visualiser_enabled: e.target.checked })
              }
              className="h-5 w-5 accent-violet-500"
            />
            <span>
              <span className="block text-sm font-semibold">
                AI room visualiser on the website
              </span>
              <span className="block text-xs text-zinc-500">
                {form.visualiser_enabled !== false
                  ? "On — shoppers can render this remnant into a room from its public page"
                  : "Off — the visualiser is hidden on this remnant's public page"}
              </span>
            </span>
          </label>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">
              Notes
            </Label>
            <Textarea
              data-testid="remnant-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={4}
              placeholder="Chip on back corner, polished edge on long side…"
              className="border-zinc-800 bg-zinc-900 text-base"
            />
          </div>

          {error && (
            <p
              data-testid="remnant-form-error"
              className="text-sm font-medium text-red-400"
            >
              {error}
            </p>
          )}

          <IntakePhotos
            files={pendingPhotos}
            onChange={setPendingPhotos}
            testPrefix="remnant-intake"
          />

          <div className="no-print fixed bottom-0 left-0 right-0 border-t border-zinc-800 bg-zinc-950 p-3 sm:static sm:border-0 sm:bg-transparent sm:p-0">
            <Button
              data-testid="save-remnant-button"
              type="submit"
              disabled={busy}
              className="h-12 w-full bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400 sm:w-auto sm:px-10"
            >
              {busy ? "Saving…" : id ? "Save Changes" : "Log Remnant"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
